import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography, Chip } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import {
  fetchSamsungAccount, samsungOAuthLogin, samsungConnectToken,
  samsungDisconnectAccount, samsungPurgeAccount, samsungSync, fetchSamsungAdminSummary,
  fetchSamsungDevices, SamsungDevice,
} from '../../services/samsungApi';
import { eventBus } from '../../services/EventBus';
import SamsungSetupHelpDialog from './SamsungSetupHelpDialog';
import SamsungTvLanDialog from './SamsungTvLanDialog';
import {
  DiscoveryCell,
  DiscoveryRegisteredTable,
  discoveryRegisteredShellSx,
  discoveryRegisteredCol as col,
  discoveryRegisteredRowSx,
  useDiscoveryColumnSort,
} from '../shared/DiscoverySortableHeader';

const ACCENT = '#2391FF';

type SamsungSortKey = 'name' | 'type' | 'ip' | 'model' | 'power' | 'status' | 'via';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onGoToDevices?: () => void;
}

const SamsungDiscoveryTab: React.FC<Props> = ({ isDemoUser, onDemoActionAttempt, onGoToDevices }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [token, setToken] = useState('');
  const [syncResult, setSyncResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [unassigned, setUnassigned] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [devices, setDevices] = useState<SamsungDevice[]>([]);
  const [lanDevice, setLanDevice] = useState<SamsungDevice | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [acc, sum, list] = await Promise.all([
        fetchSamsungAccount().catch(() => null),
        fetchSamsungAdminSummary().catch(() => ({ total: 0, unassigned_count: 0 })),
        fetchSamsungDevices().catch(() => [] as SamsungDevice[]),
      ]);
      setAccount(acc);
      setUnassigned(sum.unassigned_count || 0);
      setDevices(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const handleOAuth = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    try {
      const { auth_url } = await samsungOAuthLogin();
      window.location.href = auth_url;
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.samsungDiscovery.errorOAuth', { defaultValue: 'No se pudo iniciar OAuth' }));
      setBusy(false);
    }
  };

  const handlePat = async () => {
    if (blockDemo()) return;
    if (!token.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const acc = await samsungConnectToken(token.trim());
      setAccount(acc);
      setToken('');
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.samsungDiscovery.errorToken', { defaultValue: 'Token inválido' }));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.samsungDiscovery.disconnectConfirm', { defaultValue: '¿Desconectar cuenta SmartThings?' }))) return;
    setBusy(true);
    try {
      await samsungDisconnectAccount();
      setAccount(null);
      setSyncResult(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handlePurge = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.samsungDiscovery.purgeConfirm', {
      defaultValue: 'Se borran la cuenta y las claves. Para volver a conectar hay que cargarlas de nuevo. Las tarjetas de dispositivos no se borran.',
    }))) return;
    setBusy(true);
    try {
      await samsungPurgeAccount();
      setAccount(null);
      setSyncResult(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await samsungSync();
      setSyncResult(res);
      eventBus.emit('estructura:refrescar');
      await refresh();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || 'Error sync'));
    } finally {
      setBusy(false);
    }
  };

  const formatLastRun = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    return `${d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
  };

  const kindLabel = (kind: SamsungDevice['device_kind']) => (
    kind === 'tv'
      ? t('settings.discoveryCols.kindTv')
      : t('settings.discoveryCols.kindAc')
  );

  const viaLabel = (d: SamsungDevice) => (
    d.device_kind === 'tv' && d.control_mode === 'lan'
      ? t('settings.discoveryCols.viaLan')
      : t('settings.discoveryCols.viaSamsung')
  );

  const powerLabel = (power?: boolean | null) => {
    if (power == null) return { text: '—', color: '#888' };
    return power
      ? { text: t('settings.discoveryCols.powerOn'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.powerOff'), color: '#999' };
  };

  const statusLabel = (d: SamsungDevice) => {
    if (d.missing_from_cloud) {
      return { text: t('settings.discoveryCols.statusMissing'), color: '#e5a54b' };
    }
    return d.online
      ? { text: t('settings.discoveryCols.statusOnline'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.statusOffline'), color: '#999' };
  };

  const getSortValue = useCallback((d: SamsungDevice, key: SamsungSortKey) => {
    if (key === 'name') return d.nombre || '';
    if (key === 'type') return kindLabel(d.device_kind);
    if (key === 'ip') return d.lan_host || '';
    if (key === 'model') return d.modelo || '';
    if (key === 'power') return powerLabel(d.power).text;
    if (key === 'via') return viaLabel(d);
    return statusLabel(d).text;
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- labels follow t

  const { sorted: sortedDevices, sortKey, sortDir, toggleSort } = useDiscoveryColumnSort(devices, getSortValue);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>;
  }

  const connected = Boolean(account?.connected);
  const lastRun = formatLastRun(account?.last_sync?.ran_at);

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 1,
      }}
      >
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: ACCENT, flex: 1, minWidth: 0 }}>
          {t('settings.samsungDiscovery.accountTitle', { defaultValue: 'Cuenta del hogar (SmartThings)' })}
        </Typography>
        <Chip
          icon={<HelpOutline sx={{ fontSize: '14px !important' }} />}
          label={t('settings.samsungDiscovery.help.guideLabel', { defaultValue: 'Guía' })}
          size="small"
          clickable
          onClick={() => setHelpOpen(true)}
          sx={{
            height: 24,
            fontSize: 11,
            fontWeight: 700,
            color: ACCENT,
            bgcolor: 'rgba(35,145,255,0.1)',
            border: '1px solid rgba(35,145,255,0.45)',
            flexShrink: 0,
            '& .MuiChip-icon': { color: ACCENT, ml: 0.5 },
            '&:hover': { bgcolor: 'rgba(35,145,255,0.18)' },
          }}
        />
      </Box>

      {!account?.configured && (
        <Typography sx={{ color: '#e5a54b', fontSize: 12, mb: 1 }}>
          {t('settings.samsungDiscovery.oauthNotConfigured', {
            defaultValue: 'OAuth no configurado (SAMSUNG_CLIENT_ID/SECRET). Podés conectar con un token/PAT temporal abajo.',
          })}
        </Typography>
      )}

      {connected ? (
        <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 1.5, mb: 1.5, bgcolor: '#2F3235' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{account.display_name || 'SmartThings'}</Typography>
            <Chip size="small" label={t('settings.samsungDiscovery.connected', { defaultValue: 'Conectada' })} sx={{ bgcolor: 'rgba(102,187,106,0.2)', color: '#66bb6a' }} />
          </Box>
          {account.email && <Typography sx={{ fontSize: 12, color: '#aaa' }}>{account.email}</Typography>}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={handleDisconnect} disabled={busy} sx={{ color: '#e57373', borderColor: '#e57373' }}>
              {t('settings.samsungDiscovery.disconnect', { defaultValue: 'Desconectar' })}
            </Button>
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.samsungDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {account?.credentials_stored && (
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', mb: 1.5, '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.samsungDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          )}
          {account?.configured && (
            <Button fullWidth variant="contained" onClick={handleOAuth} disabled={busy} sx={{ bgcolor: ACCENT, mb: 1.5 }}>
              {busy ? <CircularProgress size={18} /> : t('settings.samsungDiscovery.connectOAuth', { defaultValue: 'Conectar con OAuth' })}
            </Button>
          )}
          <Typography sx={{ fontSize: 12, color: '#999', mb: 0.5 }}>
            {t('settings.samsungDiscovery.patHint', { defaultValue: 'O pegá un token/PAT SmartThings:' })}
          </Typography>
          <TextField
            fullWidth size="small" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder="pat-… / access token"
            sx={{ mb: 1, input: { color: 'white' }, '& .MuiOutlinedInput-root': { bgcolor: '#1a1a1a' } }}
          />
          <Button fullWidth variant="outlined" onClick={handlePat} disabled={busy || !token.trim()} sx={{ borderColor: ACCENT, color: ACCENT }}>
            {t('settings.samsungDiscovery.connectToken', { defaultValue: 'Conectar con token' })}
          </Button>
        </Box>
      )}

      {connected && (
        <>
          <Box sx={{ mb: 1.5 }}>
            {lastRun && (
              <Typography sx={{ fontSize: '0.7rem', color: '#888', mb: 1, textAlign: 'right' }}>
                {t('settings.samsungDiscovery.lastRun', { defaultValue: 'Último' })}: {lastRun}
              </Typography>
            )}
            <Button fullWidth variant="contained" onClick={handleSync} disabled={busy} sx={{ bgcolor: ACCENT }}>
              {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.samsungDiscovery.sync', { defaultValue: 'Sincronizar dispositivos' })}
            </Button>
          </Box>
          {syncResult && (
            <Typography sx={{ fontSize: 12, color: '#bbb', mb: 1.5 }}>
              +{syncResult.added} · ↻{syncResult.updated} · ausentes {syncResult.missing}
              {syncResult.errors?.length ? ` · errores ${syncResult.errors.length}` : ''}
            </Typography>
          )}
        </>
      )}

      {error && <Typography sx={{ color: '#e57373', fontSize: 12, mb: 1.5 }}>{String(error)}</Typography>}

      <Box sx={discoveryRegisteredShellSx()}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.samsungDiscovery.devicesTitle', { defaultValue: 'Dispositivos registrados' })}
        </Typography>
        {devices.length === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
            {t('settings.samsungDiscovery.emptyDevices', { defaultValue: 'Todavía no hay dispositivos sincronizados. Conectá la cuenta y sincronizá.' })}
          </Typography>
        ) : (
          <>
            <DiscoveryRegisteredTable
              columns={[
                { key: 'name', label: t('settings.discoveryCols.name'), sx: col.name },
                { key: 'type', label: t('settings.discoveryCols.type'), sx: col.type },
                { key: 'ip', label: t('settings.discoveryCols.ip'), sx: col.ip },
                { key: 'model', label: t('settings.discoveryCols.model'), sx: col.model },
                { key: 'power', label: t('settings.discoveryCols.power'), sx: col.power },
                { key: 'status', label: t('settings.discoveryCols.status'), sx: col.status },
                { key: 'via', label: t('settings.discoveryCols.via'), sx: col.via },
              ]}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggle={toggleSort}
            >
            {sortedDevices.map((d) => {
              const pl = powerLabel(d.power);
              const sl = statusLabel(d);
              const via = viaLabel(d);
              return (
                <Box key={d.id} sx={discoveryRegisteredRowSx}>
                  <DiscoveryCell label={t('settings.discoveryCols.name')} sx={col.name} title={d.nombre}>{d.nombre}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.type')} sx={col.type}>{kindLabel(d.device_kind)}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.ip')} sx={col.ip} title={d.lan_host || undefined}>{d.lan_host || '—'}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.model')} sx={col.model} title={d.modelo || undefined}>{d.modelo || '—'}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.power')} sx={{ ...col.power, color: pl.color }}>{pl.text}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.status')} sx={{ ...col.status, color: sl.color }}>{sl.text}</DiscoveryCell>
                  <DiscoveryCell
                    label={t('settings.discoveryCols.via')}
                    sx={d.device_kind === 'tv'
                      ? { ...col.via, color: ACCENT, textDecoration: 'underline' }
                      : col.via}
                    onClick={d.device_kind === 'tv' ? () => setLanDevice(d) : undefined}
                  >
                    {via}
                  </DiscoveryCell>
                </Box>
              );
            })}
            </DiscoveryRegisteredTable>
          </>
        )}
      </Box>

      {unassigned > 0 && onGoToDevices && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={t('settings.samsungDiscovery.goToDevices', { defaultValue: 'Ir a Dispositivos ({{count}} sin asignar)', count: unassigned })}
            onClick={onGoToDevices}
            clickable
            sx={{ backgroundColor: '#262626', color: '#fff' }}
          />
        </Box>
      )}

      <SamsungSetupHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <SamsungTvLanDialog
        device={lanDevice}
        open={Boolean(lanDevice)}
        onClose={() => setLanDevice(null)}
        onChanged={(updated) => {
          setLanDevice(updated);
          setDevices((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
        }}
      />
    </Box>
  );
};

export default SamsungDiscoveryTab;
